/**
 * 🍦 Zustand Store
 * Cart and User state management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Cart Types
export interface CartItem {
  id: string;
  menuItemId: string;
  name: string;
  price: number;
  quantity: number;
  size?: 'small' | 'large';
  container?: 'cone' | 'cup';
  toppings?: string[];
  imageUrl?: string;
}

interface CartStore {
  items: CartItem[];
  lastOrder: CartItem[];
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  updateToppings: (id: string, toppings: string[]) => void;
  updateSize: (id: string, size: 'small' | 'large') => void;
  updateContainer: (id: string, container: 'cone' | 'cup') => void;
  clearCart: () => void;
  checkout: () => void;
  totalItems: () => number;
  subtotal: () => number;
  serviceFee: number;
  total: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      lastOrder: [],
      serviceFee: 0.50,

      addItem: (item) => {
        const id = `cart-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;
        set((state) => ({
          items: [...state.items, { ...item, id }],
        }));
      },

      removeItem: (id) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== id),
        }));
      },

      updateQuantity: (id, quantity) => {
        if (quantity <= 0) {
          get().removeItem(id);
          return;
        }
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, quantity } : item
          ),
        }));
      },

      updateToppings: (id, toppings) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, toppings } : item
          ),
        }));
      },

      updateSize: (id, size) => {
        set((state) => ({
          items: state.items.map((item) => {
            if (item.id !== id) return item;
            // Only adjust price if size actually changed
            if (item.size === size) return item;
            const priceDiff = size === 'large' ? 2 : -2;
            return { ...item, size, price: item.price + priceDiff };
          }),
        }));
      },

      updateContainer: (id, container) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, container } : item
          ),
        }));
      },

      clearCart: () => set({ items: [] }),

      checkout: () => {
        set((state) => ({
          lastOrder: [...state.items],
          items: [],
        }));
      },
      
      totalItems: () => get().items.reduce((sum, item) => sum + item.quantity, 0),
      
      subtotal: () => {
        return get().items.reduce((sum, item) => {
          let itemTotal = item.price * item.quantity;
          if (item.toppings) {
            itemTotal += item.toppings.length * 1.5 * item.quantity;
          }
          return sum + itemTotal;
        }, 0);
      },
      
      total: () => get().subtotal() + get().serviceFee,
    }),
    {
      name: 'frosty-cart',
    }
  )
);

// Order Types
export interface Order {
  id: string;
  orderNumber: string;
  items: CartItem[];
  subtotal: number;
  serviceFee: number;
  deliveryFee?: number;
  total: number;
  status: 'pending' | 'preparing' | 'ready' | 'collected';
  pickupWindow: string;
  isDelivery?: boolean;
  createdAt: string;
  seatInfo?: SeatInfo | null;
}

interface OrderStore {
  orderHistory: Order[];
  addOrder: (order: Omit<Order, 'id' | 'orderNumber' | 'createdAt'>) => string;
  updateOrderStatus: (id: string, status: Order['status']) => void;
  getOrder: (id: string) => Order | undefined;
  removeOrder: (id: string) => void;
  clearHistory: () => void;
}

export const useOrderStore = create<OrderStore>()(
  persist(
    (set, get) => ({
      orderHistory: [],

      addOrder: (order) => {
        const id = `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
        const orderNumber = String(Math.floor(100 + Math.random() * 900));
        const newOrder: Order = {
          ...order,
          id,
          orderNumber,
          createdAt: new Date().toISOString(),
        };
        set((state) => ({
          orderHistory: [newOrder, ...state.orderHistory],
        }));
        return id;
      },

      updateOrderStatus: (id, status) => {
        set((state) => ({
          orderHistory: state.orderHistory.map((order) =>
            order.id === id ? { ...order, status } : order
          ),
        }));
      },

      getOrder: (id) => {
        return get().orderHistory.find((order) => order.id === id);
      },

      removeOrder: (id) => {
        set((state) => ({
          orderHistory: state.orderHistory.filter((order) => order.id !== id),
        }));
      },

      clearHistory: () => set({ orderHistory: [] }),
    }),
    {
      name: 'frosty-orders',
    }
  )
);

// User Types
interface SeatInfo {
  section: string;
  row: string;
  number: string;
}

interface UserStore {
  userId: string | null;
  seatInfo: SeatInfo | null;
  familyGroupId: string | null;
  setUser: (userId: string) => void;
  setSeatInfo: (seat: SeatInfo) => void;
  clearSeatInfo: () => void;
  setFamilyGroup: (groupId: string) => void;
  logout: () => void;
}

export const useUserStore = create<UserStore>()(
  persist(
    (set) => ({
      userId: null,
      seatInfo: null,
      familyGroupId: null,
      
      setUser: (userId) => set({ userId }),
      setSeatInfo: (seat) => set({ seatInfo: seat }),
      clearSeatInfo: () => set({ seatInfo: null }),
      setFamilyGroup: (groupId) => set({ familyGroupId: groupId }),
      logout: () => set({ userId: null, seatInfo: null, familyGroupId: null }),
    }),
    {
      name: 'frosty-user',
    }
  )
);

// Contest Types
export interface Contest {
  id: string;
  prize: string;
  prizeEmoji: string;
  endTime: string;
  isActive: boolean;
  winnerId: string | null;
  winnerName: string | null;
  entries: string[]; // order IDs that entered
  createdAt: string;
}

interface ContestStore {
  contests: Contest[];
  createContest: (prize: string, prizeEmoji: string, durationMinutes: number) => string;
  endContest: (contestId: string) => void;
  pickWinner: (contestId: string) => void;
  enterContest: (contestId: string, oderId: string) => void;
  getActiveContest: () => Contest | undefined;
  hasEntered: (contestId: string, oderId: string) => boolean;
  clearContests: () => void;
}

export const useContestStore = create<ContestStore>()(
  persist(
    (set, get) => ({
      contests: [],

      createContest: (prize, prizeEmoji, durationMinutes) => {
        const id = `CONTEST-${Date.now()}`;
        const endTime = new Date(Date.now() + durationMinutes * 60 * 1000).toISOString();
        const newContest: Contest = {
          id,
          prize,
          prizeEmoji,
          endTime,
          isActive: true,
          winnerId: null,
          winnerName: null,
          entries: [],
          createdAt: new Date().toISOString(),
        };
        set((state) => ({
          contests: [newContest, ...state.contests],
        }));
        return id;
      },

      endContest: (contestId) => {
        set((state) => ({
          contests: state.contests.map((c) =>
            c.id === contestId ? { ...c, isActive: false } : c
          ),
        }));
      },

      pickWinner: (contestId) => {
        const contest = get().contests.find((c) => c.id === contestId);
        if (!contest || contest.entries.length === 0) return;

        const randomIndex = Math.floor(Math.random() * contest.entries.length);
        const winnerId = contest.entries[randomIndex];

        set((state) => ({
          contests: state.contests.map((c) =>
            c.id === contestId
              ? { ...c, isActive: false, winnerId, winnerName: `Order #${winnerId.slice(-3)}` }
              : c
          ),
        }));
      },

      enterContest: (contestId, oderId) => {
        set((state) => ({
          contests: state.contests.map((c) =>
            c.id === contestId && !c.entries.includes(oderId)
              ? { ...c, entries: [...c.entries, oderId] }
              : c
          ),
        }));
      },

      getActiveContest: () => {
        return get().contests.find((c) => c.isActive && new Date(c.endTime) > new Date());
      },

      hasEntered: (contestId, oderId) => {
        const contest = get().contests.find((c) => c.id === contestId);
        return contest ? contest.entries.includes(oderId) : false;
      },

      clearContests: () => set({ contests: [] }),
    }),
    {
      name: 'frosty-contests',
    }
  )
);

// Settings Store (Admin controls)
interface SettingsStore {
  deliveryEnabled: boolean;
  deliveryFee: number;
  setDeliveryEnabled: (enabled: boolean) => void;
  setDeliveryFee: (fee: number) => void;
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      deliveryEnabled: false,
      deliveryFee: 5.00,
      setDeliveryEnabled: (enabled) => set({ deliveryEnabled: enabled }),
      setDeliveryFee: (fee) => set({ deliveryFee: fee }),
    }),
    {
      name: 'frosty-settings',
    }
  )
);

// API Base URL
export const API_BASE_URL = import.meta.env.PROD
  ? 'https://frosty-flow-api.truthconsultingint.workers.dev'
  : 'http://localhost:8787';

// People Pixels Server URL (Cloudflare Worker)
export const PIXEL_SERVER_URL = import.meta.env.PROD
  ? 'https://people-pixels.truthconsultingint.workers.dev'
  : 'http://localhost:8788';

// People Pixels Store
interface PixelEvent {
  id: string;
  name: string;
  color?: string; // For phone-up mode
  mode: 'phone_up' | 'stand_sit';
  reward: string;
  triggeredAt?: string;
}

interface PixelServerStats {
  totalConnections: number;
  sections: Record<string, number>;
  currentEvent: string | null;
  uptime: number;
}

interface PeoplePixelsStore {
  isEnabled: boolean;
  connectedFans: number;
  currentEvent: PixelEvent | null;
  serverStats: PixelServerStats | null;
  lastError: string | null;
  setEnabled: (enabled: boolean) => void;
  setConnectedFans: (count: number) => void;
  triggerBuzz: () => void;
  triggerEvent: (event: PixelEvent) => void;
  clearEvent: () => void;
  // Server API calls
  sendBuzzToServer: () => Promise<boolean>;
  sendColorToServer: (color: string, duration?: number) => Promise<boolean>;
  sendClearToServer: () => Promise<boolean>;
  fetchServerStats: () => Promise<void>;
}

// BroadcastChannel for cross-tab communication
const pixelChannel = typeof window !== 'undefined' ? new BroadcastChannel('people-pixels') : null;

export const usePeoplePixelsStore = create<PeoplePixelsStore>()(
  persist(
    (set, get) => ({
      isEnabled: true,
      connectedFans: 0,
      currentEvent: null,
      serverStats: null,
      lastError: null,

      setEnabled: (enabled) => set({ isEnabled: enabled }),
      setConnectedFans: (count) => set({ connectedFans: count }),

      triggerBuzz: () => {
        // Broadcast buzz to all connected tabs/windows (local fallback)
        if (pixelChannel) {
          pixelChannel.postMessage({ type: 'BUZZ', timestamp: Date.now() });
        }
        // Also trigger locally for same-tab testing
        window.dispatchEvent(new CustomEvent('pixel-buzz'));
      },

      triggerEvent: (event) => {
        const eventWithTime = { ...event, triggeredAt: new Date().toISOString() };
        set({ currentEvent: eventWithTime });
        if (pixelChannel) {
          pixelChannel.postMessage({ type: 'EVENT', event: eventWithTime });
        }
        window.dispatchEvent(new CustomEvent('pixel-event', { detail: eventWithTime }));
      },

      clearEvent: () => set({ currentEvent: null }),

      // Send BUZZ to all connected phones via server
      sendBuzzToServer: async () => {
        try {
          set({ lastError: null });
          const response = await fetch(`${PIXEL_SERVER_URL}/admin/buzz`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pattern: [200, 100, 200, 100, 200] }),
          });

          if (!response.ok) {
            throw new Error(`Server error: ${response.status}`);
          }

          const result = await response.json();
          console.log('[PeoplePixels] Buzz sent to server:', result);

          // Also trigger locally as fallback
          get().triggerBuzz();
          return true;
        } catch (error) {
          console.error('[PeoplePixels] Failed to send buzz to server:', error);
          set({ lastError: error instanceof Error ? error.message : 'Failed to send buzz' });
          // Fallback to local broadcast
          get().triggerBuzz();
          return false;
        }
      },

      // Send COLOR to all connected phones via server
      sendColorToServer: async (color: string, duration?: number) => {
        try {
          set({ lastError: null });
          const response = await fetch(`${PIXEL_SERVER_URL}/admin/color`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ color, duration }),
          });

          if (!response.ok) {
            throw new Error(`Server error: ${response.status}`);
          }

          const result = await response.json();
          console.log('[PeoplePixels] Color sent to server:', result);
          return true;
        } catch (error) {
          console.error('[PeoplePixels] Failed to send color to server:', error);
          set({ lastError: error instanceof Error ? error.message : 'Failed to send color' });
          return false;
        }
      },

      // Send CLEAR to all connected phones via server
      sendClearToServer: async () => {
        try {
          set({ lastError: null });
          const response = await fetch(`${PIXEL_SERVER_URL}/admin/clear`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });

          if (!response.ok) {
            throw new Error(`Server error: ${response.status}`);
          }

          const result = await response.json();
          console.log('[PeoplePixels] Clear sent to server:', result);
          return true;
        } catch (error) {
          console.error('[PeoplePixels] Failed to send clear to server:', error);
          set({ lastError: error instanceof Error ? error.message : 'Failed to send clear' });
          return false;
        }
      },

      // Fetch real-time stats from server
      fetchServerStats: async () => {
        try {
          const response = await fetch(`${PIXEL_SERVER_URL}/admin/state`);
          if (!response.ok) {
            throw new Error(`Server error: ${response.status}`);
          }

          const data = await response.json();
          set({
            serverStats: {
              totalConnections: data.totalConnections || 0,
              sections: data.sections || {},
              currentEvent: data.currentEvent?.type || null,
              uptime: data.uptime || 0,
            },
            connectedFans: data.totalConnections || 0,
            lastError: null,
          });
        } catch (error) {
          console.error('[PeoplePixels] Failed to fetch server stats:', error);
          set({ lastError: error instanceof Error ? error.message : 'Failed to fetch stats' });
        }
      },
    }),
    {
      name: 'frosty-pixels',
    }
  )
);

// Listen for broadcasts from other tabs
if (pixelChannel) {
  pixelChannel.onmessage = (event) => {
    if (event.data.type === 'BUZZ') {
      window.dispatchEvent(new CustomEvent('pixel-buzz'));
    } else if (event.data.type === 'EVENT') {
      window.dispatchEvent(new CustomEvent('pixel-event', { detail: event.data.event }));
    }
  };
}

// Meeting Spots Store (Family meeting points)
export interface MeetingSpot {
  id: string;
  name: string;
  x: number;
  y: number;
  emoji: string;
  createdAt: string;
}

interface MeetingSpotsStore {
  spots: MeetingSpot[];
  addSpot: (spot: Omit<MeetingSpot, 'id' | 'createdAt'>) => string;
  updateSpot: (id: string, updates: Partial<Omit<MeetingSpot, 'id' | 'createdAt'>>) => void;
  removeSpot: (id: string) => void;
  clearSpots: () => void;
}

export const useMeetingSpotsStore = create<MeetingSpotsStore>()(
  persist(
    (set) => ({
      spots: [],

      addSpot: (spot) => {
        const id = `SPOT-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;
        const newSpot: MeetingSpot = {
          ...spot,
          id,
          createdAt: new Date().toISOString(),
        };
        set((state) => ({
          spots: [...state.spots, newSpot],
        }));
        return id;
      },

      updateSpot: (id, updates) => {
        set((state) => ({
          spots: state.spots.map((spot) =>
            spot.id === id ? { ...spot, ...updates } : spot
          ),
        }));
      },

      removeSpot: (id) => {
        set((state) => ({
          spots: state.spots.filter((spot) => spot.id !== id),
        }));
      },

      clearSpots: () => set({ spots: [] }),
    }),
    {
      name: 'frosty-meeting-spots',
    }
  )
);
